import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/store/gameStore';
import { getCurrentQuizConfig } from '@/config/quizConfig';
import clsx from 'clsx';

export const QuizModal: React.FC = () => {
  const { 
    currentQuiz, 
    answerQuiz, 
    skipQuiz, 
    nextQuiz, 
    finishQuizRound,
    getQuizProgress,
    isInQuizRound 
  } = useGameStore();
  
  const config = getCurrentQuizConfig();
  
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [timeLeft, setTimeLeft] = useState(config.timePerQuestion);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  
  // 重置状态当新题目出现时
  useEffect(() => {
    if (currentQuiz) {
      setSelectedAnswer(null);
      setShowResult(false);
      setIsCorrect(false);
      setTimeLeft(config.timePerQuestion);
      setQuestionStartTime(Date.now());
    }
  }, [currentQuiz, config.timePerQuestion]);
  
  // 倒计时
  useEffect(() => {
    if (!currentQuiz || showResult) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // 时间到，自动跳过
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [currentQuiz, showResult]);
  
  const handleAnswerSelect = (answerIndex: number) => {
    if (showResult) return;
    setSelectedAnswer(answerIndex);
  };
  
  const handleSubmit = () => {
    if (selectedAnswer === null || !currentQuiz) return;
    
    const timeUsed = Math.round((Date.now() - questionStartTime) / 1000);
    const correct = answerQuiz(selectedAnswer, timeUsed);
    setIsCorrect(correct);
    setShowResult(true);
    
    // 根据配置决定是否自动进入下一题
    if (config.autoNext) {
      setTimeout(() => {
        handleNext();
      }, config.autoNextDelay);
    }
  };
  
  const handleSkip = () => {
    skipQuiz();
    handleNext();
  };
  
  const handleTimeUp = () => {
    if (!currentQuiz) return;
    
    // 时间到自动跳过
    skipQuiz();
    setShowResult(true);
    setIsCorrect(false);
    
    setTimeout(() => {
      handleNext();
    }, 2000);
  };
  
  const handleNext = () => {
    const progress = getQuizProgress();
    
    if (progress.current < progress.total) {
      // 还有下一题
      nextQuiz();
    } else {
      // 所有题目完成
      finishQuizRound();
    }
    
    setShowResult(false);
    setSelectedAnswer(null);
  };
  
  if (!currentQuiz || !isInQuizRound) return null;
  
  const progress = getQuizProgress();
  
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* 头部 */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">🧠</span>
                <div>
                  <h3 className="text-xl font-bold">地理知识问答</h3>
                  <p className="text-blue-100 text-sm">
                    第 {progress.current} 题，共 {progress.total} 题
                  </p>
                </div>
              </div>
              
              {/* 进度条 */}
              <div className="flex flex-col items-end space-y-2">
                <div className="w-32 h-2 bg-blue-400 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-white rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${(progress.current / progress.total) * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                
                {/* 倒计时 */}
                <motion.div
                  className={clsx(
                    'text-2xl font-bold',
                    {
                      'text-white': timeLeft > 10,
                      'text-yellow-300': timeLeft <= 10 && timeLeft > 5,
                      'text-red-300': timeLeft <= 5
                    }
                  )}
                  animate={timeLeft <= 5 ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5, repeat: timeLeft <= 5 ? Infinity : 0 }}
                >
                  {timeLeft}s
                </motion.div>
              </div>
            </div>
          </div>
          
          {/* 问题内容 */}
          <div className="p-6">
            {!showResult ? (
              <>
                {/* 问题 */}
                <div className="mb-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {currentQuiz.type === 'capital' && '省会城市'}
                      {currentQuiz.type === 'abbreviation' && '省份简称'}
                      {currentQuiz.type === 'neighbors' && '相邻省份'}
                      {currentQuiz.type === 'landmarks' && '地理标志'}
                    </span>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-800 leading-relaxed">
                    {currentQuiz.question}
                  </h4>
                </div>
                
                {/* 选项 */}
                <div className="space-y-3 mb-6">
                  {currentQuiz.options.map((option, index) => (
                    <motion.button
                      key={index}
                      className={clsx(
                        'w-full p-4 text-left rounded-xl border-2 transition-all duration-200',
                        {
                          'border-blue-500 bg-blue-50': selectedAnswer === index,
                          'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100': selectedAnswer !== index
                        }
                      )}
                      onClick={() => handleAnswerSelect(index)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={clsx(
                          'w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold',
                          {
                            'border-blue-500 bg-blue-500 text-white': selectedAnswer === index,
                            'border-gray-300 text-gray-500': selectedAnswer !== index
                          }
                        )}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span className="text-gray-800 font-medium">{option}</span>
                      </div>
                    </motion.button>
                  ))}
                </div>
                
                {/* 操作按钮 */}
                <div className="flex items-center justify-between">
                  <motion.button
                    className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
                    onClick={handleSkip}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    跳过题目
                  </motion.button>
                  
                  <motion.button
                    className={clsx(
                      'px-8 py-3 rounded-xl font-bold transition-all duration-200',
                      {
                        'bg-blue-500 text-white hover:bg-blue-600': selectedAnswer !== null,
                        'bg-gray-300 text-gray-500 cursor-not-allowed': selectedAnswer === null
                      }
                    )}
                    onClick={handleSubmit}
                    disabled={selectedAnswer === null}
                    whileHover={selectedAnswer !== null ? { scale: 1.05 } : {}}
                    whileTap={selectedAnswer !== null ? { scale: 0.95 } : {}}
                  >
                    确认答案
                  </motion.button>
                </div>
              </>
            ) : (
              /* 答题结果 */
              <motion.div
                className="text-center py-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  className="text-8xl mb-4"
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ duration: 0.6 }}
                >
                  {timeLeft === 0 ? '⏰' : (isCorrect ? '🎉' : '😅')}
                </motion.div>
                
                <h3 className={clsx(
                  'text-2xl font-bold mb-2',
                  {
                    'text-green-600': isCorrect,
                    'text-orange-600': !isCorrect && timeLeft > 0,
                    'text-red-600': timeLeft === 0
                  }
                )}>
                  {timeLeft === 0 ? '时间到！' : (isCorrect ? '回答正确！' : '答案错误')}
                </h3>
                
                <div className="mb-4">
                  {isCorrect ? (
                    <p className="text-green-600 font-medium">
                      +{config.correctScore} 分
                      {config.timeBonus && timeLeft > 0 && (
                        <span className="text-sm ml-2">
                          (含时间奖励)
                        </span>
                      )}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {config.showCorrectAnswer && (
                        <p className="text-gray-600">
                          正确答案是: <span className="font-bold text-green-600">
                            {String.fromCharCode(65 + currentQuiz.correctAnswer)}. {currentQuiz.options[currentQuiz.correctAnswer]}
                          </span>
                        </p>
                      )}
                      {config.showSelectedAnswer && selectedAnswer !== null && selectedAnswer !== -1 && (
                        <p className="text-gray-500 text-sm">
                          你的答案: {String.fromCharCode(65 + selectedAnswer)}. {currentQuiz.options[selectedAnswer]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* 解释说明 */}
                {config.showExplanation && currentQuiz.explanation && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left mb-4">
                    <div className="flex items-start space-x-2">
                      <span className="text-blue-500 mt-1">💡</span>
                      <div>
                        <div className="font-medium text-blue-800 mb-1">知识点</div>
                        <div className="text-blue-700 text-sm leading-relaxed">
                          {currentQuiz.explanation}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* 下一题按钮或完成提示 */}
                {!config.autoNext && (
                  <motion.button
                    className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
                    onClick={handleNext}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {progress.current < progress.total ? '下一题' : '完成问答'}
                  </motion.button>
                )}
                
                {config.autoNext && (
                  <motion.div
                    className="mt-4 text-sm text-gray-500"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                  >
                    {progress.current < progress.total ? 
                      `${Math.ceil(config.autoNextDelay / 1000)}秒后自动进入下一题...` : 
                      `${Math.ceil(config.autoNextDelay / 1000)}秒后自动完成问答...`
                    }
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
